import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type Item from '../../@types/item';
import { urls } from '../../helpers';
import { getMarket } from '../../i18n';
import { getRetailerDocument } from '../../selectors/retailer';
import Checklist from '../../styled-components/components/Checklist';
import Content from '../../styled-components/components/Content';
import { IconCheck } from '../../styled-components/components/Icon';
import { Image } from '../../styled-components/components/Image';
import { Link } from '../../styled-components/components/Link';
import TabBox from '../../styled-components/components/TabBox';
import { getAutomotiveAssociationName } from '../../utils/market';
import { dateMonthYear } from '../../utils/ts/date';

interface SafetyDataProps {
  item: Item;
}

const SafetyData = ({ item }: SafetyDataProps) => {
  const { t } = useTranslation(['common', 'item']);
  const automotiveAssociation = getAutomotiveAssociationName();
  const marketCode = getMarket().marketCode;
  const retailer = useSelector(getRetailerDocument);

  // Automotive association
  const automotiveAssociationLinkUrl =
    marketCode === 'NO' ? 'https://www.nbf.no/om-oss/handle-trygt/' : 'https://mrf.se';
  const automotiveAssociationLogoMaxWidth = marketCode === 'NO' ? '80px' : '40px';

  // Condition Report
  const conditionReport = item.documents.find((doc) => doc.category === 'content declaration');

  const itemsList = [
    {
      faded: !retailer?.isConnectedToMrf,
      heading: t('safetyData.automotiveAssociation.heading', { ns: 'item' }),
      body: (
        <>
          {retailer?.isConnectedToMrf ? (
            <>
              <Image
                src={`/images/logotypes/${automotiveAssociation}/${automotiveAssociation}_logo.png`}
                srcSet={`/images/logotypes/${automotiveAssociation}/${automotiveAssociation}_logo@2x.png 2x`}
                alt={t('safetyData.automotiveAssociation.logoAlt', { ns: 'item' })}
                style={{ maxWidth: automotiveAssociationLogoMaxWidth }}
              />
              <Content>
                <p>{t('safetyData.automotiveAssociation.body', { ns: 'item' })}</p>
              </Content>
              <Link
                href={automotiveAssociationLinkUrl}
                title={t('safetyData.automotiveAssociation.link.title', { ns: 'item' })}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('safetyData.automotiveAssociation.link.text', { ns: 'item' })}
              </Link>
            </>
          ) : (
            <Content>
              <p>{t('safetyData.automotiveAssociation.noInformation', { ns: 'item' })}</p>
            </Content>
          )}
        </>
      ),
    },
  ];

  // Condition Report
  const showConditionReport = marketCode === 'NO';
  if (showConditionReport) {
    itemsList.push({
      faded: !conditionReport,
      heading: t('safetyData.conditionReport.heading', { ns: 'item' }),
      body: (
        <>
          {conditionReport ? (
            <>
              <Content>
                <p>{t('safetyData.conditionReport.body', { ns: 'item' })}</p>
              </Content>
              <Link
                href={conditionReport.url}
                title={t('safetyData.conditionReport.link.title', { ns: 'item' })}
                target="_blank"
                rel="noopener noreferrer"
                $noExternalIcon
              >
                {t('safetyData.conditionReport.link.text', { ns: 'item' })} (PDF)
              </Link>
            </>
          ) : (
            <Content>
              <p>{t('safetyData.conditionReport.noInformation', { ns: 'item' })}</p>
            </Content>
          )}
        </>
      ),
    });
  }

  // Warranty
  const showWarranty = marketCode === 'NO';
  if (showWarranty) {
    itemsList.push({
      faded: !item.warranty,
      heading: t('safetyData.warranty.heading', { ns: 'item' }),
      body: (
        <>
          {item.warranty ? (
            <Content>
              <p>
                {item.warranty.durationStartAt !== '0001-01-01T00:00:00'
                  ? t('safetyData.warranty.bodyDurationStartAt', {
                      ns: 'item',
                      maxDuration: t('safetyData.warranty.duration', {
                        ns: 'item',
                        count: item.warranty.maxDuration.value,
                        unit:
                          item.warranty.maxDuration.unit === 'Month'
                            ? t('unitMonth', {
                                ns: 'common',
                                count: item.warranty.maxDuration.value,
                              }).toLowerCase()
                            : t('unitYear', {
                                ns: 'common',
                                count: item.warranty.maxDuration.value,
                              }).toLowerCase(),
                      }),
                      maxDistance: t('safetyData.warranty.distance', {
                        ns: 'item',
                        count: item.warranty.maxDistance.value,
                        unit:
                          item.warranty.maxDistance.unit === 'Kilometer'
                            ? t('km', { ns: 'common' })
                            : t('mil', { ns: 'common' }),
                      }),
                      durationStartAt: dateMonthYear(item.warranty.durationStartAt),
                    })
                  : t('safetyData.warranty.bodyDefault', {
                      ns: 'item',
                      maxDuration: t('safetyData.warranty.duration', {
                        ns: 'item',
                        count: item.warranty.maxDuration.value,
                        unit:
                          item.warranty.maxDuration.unit === 'Month'
                            ? t('unitMonth', {
                                ns: 'common',
                                count: item.warranty.maxDuration.value,
                              }).toLowerCase()
                            : t('unitYear', {
                                ns: 'common',
                                count: item.warranty.maxDuration.value,
                              }).toLowerCase(),
                      }),
                      maxDistance: t('safetyData.warranty.distance', {
                        ns: 'item',
                        count: item.warranty.maxDistance.value,
                        unit:
                          item.warranty.maxDistance.unit === 'Kilometer'
                            ? t('km', { ns: 'common' })
                            : t('mil', { ns: 'common' }),
                      }),
                    })}
              </p>
            </Content>
          ) : (
            <Content>
              <p>{t('safetyData.warranty.noInformation', { ns: 'item' })}</p>
            </Content>
          )}
        </>
      ),
    });
  }

  // Service History
  const showServiceHistory = marketCode === 'NO';
  if (showServiceHistory) {
    itemsList.push({
      faded:
        !item.serviceHistory?.isServiceHistoryAvailable &&
        !item.serviceHistory?.isServicePlanFollowed,
      heading: t('safetyData.service.heading', { ns: 'item' }),
      body: (
        <>
          {item.serviceHistory?.isServiceHistoryAvailable ||
          item.serviceHistory?.isServicePlanFollowed ? (
            <Content>
              <p>
                {item.serviceHistory.isServiceHistoryAvailable
                  ? t('safetyData.service.historyAvailable', { ns: 'item' })
                  : undefined}
                {item.serviceHistory.isServiceHistoryAvailable &&
                  item.serviceHistory.isServicePlanFollowed &&
                  ' '}
                {item.serviceHistory.isServicePlanFollowed
                  ? t('safetyData.service.servicePlanFollowed', { ns: 'item' })
                  : undefined}
              </p>
            </Content>
          ) : (
            <Content>
              <p>{t('safetyData.service.noInformation', { ns: 'item' })}</p>
            </Content>
          )}
        </>
      ),
    });
  }

  // Right to exchange
  const showRightToExchange = marketCode === 'NO';
  if (showRightToExchange) {
    itemsList.push({
      faded: !item.rightToExchange,
      heading: t('safetyData.rightOfExchange.heading', { ns: 'item' }),
      body: (
        <>
          {item.rightToExchange ? (
            <Content>
              <p>{t('safetyData.rightOfExchange.body', { ns: 'item' })}</p>
            </Content>
          ) : (
            <Content>
              <p>{t('safetyData.rightOfExchange.noInformation', { ns: 'item' })}</p>
            </Content>
          )}
        </>
      ),
    });
  }

  // Carfax
  const showCarfax = marketCode === 'SE';
  const carfaxUsps: string[] = t('safetyData.carfax.usps', {
    ns: 'item',
    returnObjects: true,
  }) as string[];
  const isVirtualCar = item.inventoryStatusString?.toLowerCase() === 'order';

  if (showCarfax) {
    itemsList.unshift({
      faded: isVirtualCar,
      heading: t('safetyData.carfax.heading', { ns: 'item' }),
      body: (
        <>
          <Image
            src={urls.cdn('/images/logotypes/carfax/carfax_logo.jpg')}
            srcSet={`${urls.cdn('/images/logotypes/carfax/carfax_logo@2x.jpg')} 2x`}
            alt={t('safetyData.carfax.logoAlt', { ns: 'item' })}
            style={{ width: '85px' }}
          />

          {isVirtualCar ? (
            <Content>
              <p>{t('safetyData.carfax.virtualCar', { ns: 'item' })}</p>
            </Content>
          ) : (
            <>
              <Content>
                <p>{t('safetyData.carfax.body', { ns: 'item' })}</p>
              </Content>
              {carfaxUsps && <Checklist icon={<IconCheck />} spacing="small" items={carfaxUsps} />}
            </>
          )}
        </>
      ),
    });
  }

  // Place disabled items last
  const sortedItemsList = itemsList.sort((a, b) => {
    if (a.faded && !b.faded) {
      return 1;
    } else if (!a.faded && b.faded) {
      return -1;
    }
    return 0;
  });

  return (
    <div className="item-section item-section-no-padding-lt-md">
      <TabBox
        idBase="safety-data"
        ariaLabel={t('safetyData.ariaLabel', { ns: 'item' })}
        items={sortedItemsList}
      />
    </div>
  );
};

export default SafetyData;
